import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Loader2, Inbox, MapPin, Route as RouteIcon, Store, Mail, Phone } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const STATUSES = ["new", "contacted", "partner_recruited", "converted", "closed"];
const STATUS_STYLE = {
  new: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  contacted: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  partner_recruited: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  converted: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  closed: "bg-muted text-muted-foreground border-border",
};
const label = (s) => s.replace(/_/g, " ");

export default function CoverageRequestsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/coverage-requests", { params: filter === "all" ? {} : { status: filter } }); setRows(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]);

  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/coverage-requests/${id}`, { status }); setRows((r) => r.map((x) => (x.id === id ? { ...x, status } : x))); toast.success(`Marked ${label(status)}`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="coverage-requests-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-lg font-bold flex items-center gap-2"><Inbox className="w-5 h-5 text-primary" />Coverage Requests</h2>
          <p className="text-sm text-muted-foreground">Businesses that tried to advertise outside active territory — leads for sales and partner expansion.</p>
        </div>
        <div className="w-48">
          <Label className="text-xs text-muted-foreground">Status</Label>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="coverage-filter-status" className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{label(s)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        : rows.length === 0 ? <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center text-muted-foreground" data-testid="coverage-empty">No coverage requests{filter !== "all" ? ` with status "${label(filter)}"` : ""} yet.</div>
        : (
        <div className="grid md:grid-cols-2 gap-4">
          {rows.map((r) => (
            <div key={r.id} className="rounded-2xl border border-border bg-card p-4" data-testid={`coverage-card-${r.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-heading font-bold">{r.business_name}</div>
                  <div className="text-xs text-muted-foreground">{[r.city, r.state].filter(Boolean).join(", ") || "Unknown area"} · {new Date(r.created_at).toLocaleDateString()}</div>
                </div>
                <Badge className={`text-[10px] border ${STATUS_STYLE[r.status] || STATUS_STYLE.new}`} data-testid={`coverage-status-badge-${r.id}`}>{label(r.status)}</Badge>
              </div>
              <div className="mt-3 space-y-1 text-sm">
                <div className="flex items-center gap-2"><span className="text-muted-foreground">Contact:</span> {r.contact_name}</div>
                <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-muted-foreground" /><a href={`mailto:${r.email}`} className="text-primary hover:underline">{r.email}</a></div>
                {r.phone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-muted-foreground" />{r.phone}</div>}
              </div>
              <div className="mt-3 rounded-lg border border-border bg-background/40 p-2.5 space-y-1 text-xs text-muted-foreground">
                <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5" />Nearest zone: <span className="text-foreground">{r.nearest_zone_name || "—"}</span>{r.nearest_zone_distance_km != null && <span>({r.nearest_zone_distance_km} km)</span>}</div>
                <div className="flex items-center gap-2"><RouteIcon className="w-3.5 h-3.5" />Nearest path: <span className="text-foreground">{r.nearest_bike_path_name || "—"}</span></div>
                <div className="flex items-center gap-2"><Store className="w-3.5 h-3.5" />Nearest partner: <span className="text-foreground">{r.nearest_partner_name || "—"}</span></div>
              </div>
              {r.note && <p className="mt-2 text-xs text-muted-foreground italic">"{r.note}"</p>}
              <div className="mt-3">
                <Label className="text-xs text-muted-foreground">Update status</Label>
                <Select value={r.status} onValueChange={(v) => setStatus(r.id, v)}>
                  <SelectTrigger data-testid={`coverage-status-select-${r.id}`} className="mt-1 h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{label(s)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
